import { AfterViewInit, Component, ElementRef, HostListener, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { ProfileService } from "../../../account/services";
import { Company, User } from '../../../auth/models';
import { ThemeService } from '../../../common/services/theme.service';
import { UiVersionService } from '../../../v2/services/ui-version.service';
import { CeleryService } from '../../services';
import { ToastService } from '../../services/toast';

@Component({
    selector: 'navbar',
    templateUrl: './navbar.component.html',
    styleUrls: ['./navbar.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class NavbarComponent implements OnInit, AfterViewInit {
    public landingUrl = environment.landingUrl;
    public docsUrl = environment.docsUrl;
    public switchCompanies: Company[] = [];

    // Mobile drawer state (below lg). Angular-controlled instead of Bootstrap's
    // collapse so we can close it on navigation and slide it in from the left.
    public menuOpen: boolean = false;

    // Below lg the notifications bell lives in the navbar bar (not the drawer).
    // Kept as a single instance toggled by this flag so it polls only once.
    public isMobile: boolean = window.innerWidth < 992;

    constructor(
        public user: User,
        public company: Company,
        private profileService: ProfileService,
        private toast: ToastService,
        private celery: CeleryService,
        private el: ElementRef,
        public themeService: ThemeService,
        private router: Router,
        public uiVersion: UiVersionService
    ) {
    }

    public switchToV2(): void {
        void this.uiVersion.switchTo('v2');
    }

    public ngAfterViewInit(): void {
        this.updateNavbarHeight();
    }

    @HostListener('window:resize')
    public updateNavbarHeight(): void {
        this.isMobile = window.innerWidth < 992;
        const height = this.el.nativeElement.offsetHeight;
        document.documentElement.style.setProperty('--navbar-height', `${height}px`);
    }

    public toggleMenu(): void {
        this.setMenuOpen(!this.menuOpen);
    }

    public closeMenu(): void {
        this.setMenuOpen(false);
    }

    private setMenuOpen(open: boolean): void {
        this.menuOpen = open;
        // Lock body scroll while the drawer overlays the page on mobile.
        document.body.classList.toggle('mobile-nav-open', open);
    }

    @HostListener('document:keydown.escape')
    public onEscape(): void {
        if (this.menuOpen) {
            this.closeMenu();
        }
    }

    public ngOnInit(): void {
        // Close the mobile drawer whenever navigation completes — fixes the menu
        // staying open after a nav item is tapped. Dropdown toggles that don't
        // navigate (Reports/Operations) are unaffected and stay open as expected.
        this.router.events
            .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
            .subscribe(() => this.closeMenu());

        // Only load company switching for buyers who can manage
        if (this.company.isBuyer() && this.user.canManage()) {
            this.getSwitchCompanies();
        }
    }

    public switchProfile(companyId: number){
        this.profileService.switchCompanyProfile(companyId).subscribe(
            data => {
                this.toast.success('Company has been successfully switched')
                window.location.reload();
            },
            error => this.toast.error('SwitchCompanyProfile error')
        );
    }

    public getSwitchCompanies(){
        this.profileService.getCompaniesToSwitchProfile().subscribe(
            (data: any) => {
                this.switchCompanies = data;
            },
            error => {
                // Don't show error for users who simply don't have company switching access
                if (error.status === 403 || error.status === 404) {
                    this.switchCompanies = []; // Empty array, no error message
                } else {
                    console.error('Company switching error:', error);
                    this.toast.error('Unable to load company switching options');
                }
            }
        );
    }
}
